import React, {useState} from "react";
import {
	Box,
	DialogActions,
	DialogContent,
	IconButton,
	Typography,
	Dialog,
	DialogTitle,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import PieChart from "@mui/icons-material/PieChart";
import {
	NJCard,
	NJCardBody,
	NJButton,
} from "@engie-group/fluid-design-system-react";
import { format } from "date-fns";
import { useParams } from "react-router";

import PieChartClicks from "./PieChart";
import BarsChartClicks from "./BarsChart";
import {
	isDateRangeInAnyQuarter,
	mappingData,
	totalVolumePerYear,
} from "./utils";
import FluidSelect from "../../../../../common/components/FluidSelect";
import SimpleTable from "../../../../../common/components/SimpleTable";
import Toggle from "../../../../../common/components/Toggle/Toggle";

import type { NewClickOverviewT, MappedClicksDataT, ViewByT } from "./types";
import type { ClickTypesResponseType } from "../../../../../requests_cm/gecoReferentialService/types";
import {
    ContractDetailType,
    ContractPeriodClickObjectType,
    ContractPeriodType,
} from "../../../../../requests_cm/gecoContractsService/types";
import ClickModal from "../ClickModal";


import {ErrorType, formatApiErrorMessage} from "../../../../../common/utils/formatApiErrorMessage";
import {ClickFormik} from "../../../formik/clickabilityTabFormik";
import {v4 as uuidv4} from "uuid";

import {useRtkQueryDynamicEndpoint} from "../../../../../common/hooks/useRtkQueryDynamicEndpoint";
import {useGetClickPeriodsQuery} from "../../../../../requests_cm/gecoReferentialService/service";
import {useCreateContractPeriodClickObjectMutation} from "../../../../../requests_cm/gecoContractsService/service";
import {useClickModalContext} from "../ClickModalContext";

import {onCreateClickHelper} from "../onCreateClickHelper";


interface OverviewModalProps {
	contractPeriod: ContractPeriodType;
	dataClicksRaw: Array<ContractPeriodClickObjectType>;
	dataPeriodList: Array<ClickTypesResponseType>;
	handleNewClick: (newClick: NewClickOverviewT) => void;
    contractData: ContractDetailType;
	isOpen: boolean;
	handleClose?: () => void;
	setTotalVolumeAbsolute: React.Dispatch<React.SetStateAction<string>>;
}

export default function OverviewModal(props: OverviewModalProps) {
	const {
		dataPeriodList,
		dataClicksRaw,
		isOpen,
		handleClose,
		contractPeriod,
        contractData,
		setTotalVolumeAbsolute,
	} = props;
	const { contractPeriodId } = useParams();
	const [viewBy, setViewBy] = React.useState<ViewByT>("month");
	const sortedByDate = React.useMemo(
		() => mappingData(dataClicksRaw, dataPeriodList, contractPeriod),
		[dataClicksRaw, dataPeriodList, contractPeriod]
	);
	const yearsSelect = Object.keys(sortedByDate);
	const [selectedYear, setSelectedYear] = React.useState<string>(
		yearsSelect[0].toString()
	);
    const [
		createClickBase,
		{
			isLoading: isCreatingClick,
			error: clickPostError,
		},
	] = useCreateContractPeriodClickObjectMutation();

    const createClick = useRtkQueryDynamicEndpoint(createClickBase);
	const { data: clickPeriods } = useRtkQueryDynamicEndpoint(
		useGetClickPeriodsQuery
	)({});
    const {isClickModalOpen,
      openModal,
      closeModal } = useClickModalContext();

    const [newClickOverview, ] = useState<NewClickOverviewT | undefined>();
	const containFullQuarter = React.useMemo(
		() =>
			isDateRangeInAnyQuarter(
				new Date(contractPeriod.start_date),
				new Date(contractPeriod.end_date)
			),
		[contractPeriod]
	);
	const totalVolume = React.useMemo(
		() => totalVolumePerYear(sortedByDate[selectedYear]?.month || []),
		[sortedByDate, selectedYear]
	);

	React.useEffect(() => setTotalVolumeAbsolute(totalVolume.totalVolumeAbsolute), [totalVolume]);

	React.useEffect(() => setViewBy("month"), [contractPeriodId]);

	React.useEffect(() => setSelectedYear(yearsSelect[0].toString()), [contractPeriodId, yearsSelect]);

	return (
		<Dialog maxWidth="xl" onClose={handleClose} open={isOpen}>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Box>
					<IconButton color="primary">
						<PieChart />
					</IconButton>
					Clickability overview
				</Box>
				<Box>
					<IconButton aria-label="close" onClick={handleClose}>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						marginBottom: "1rem",
					}}
				>
					<FluidSelect
						isLabelStatic
						items={yearsSelect.map((year) => ({
							label: year,
							value: year,
						}))}
						label={"Select year"}
						name={"clicks_year"}
						onChange={(year) => setSelectedYear(String(year))}
						value={selectedYear}
					/>
				</Box>
				<Box
					sx={{
						border: "1px solid #EBEEF1",
						padding: "1rem",
						marginBottom: "1rem",
					}}
				>
					<Typography
						fontSize={"1.2rem"}
						fontWeight={700}
						marginBottom={2}
					>
						Clicked volume availability
					</Typography>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "3fr 5fr 2fr",
							gap: 3,
						}}
					>
						<PieChartClicks
							data={{
								clicked: {
									volume: totalVolume.totalVolumePercentage,
									volume_mwh: totalVolume.totalVolumeAbsolute,
								},
								available: {
									volume: totalVolume.availableVolumePercentage,
									volume_mwh:
										totalVolume.availableVolumeAbsolute,
								},
							}}
						/>
						<SimpleTable
							dataTestid="clickabilityTable"
							headers={[
								{ label: "Type", accessor: "type" },
								{ label: "Volume (%)", accessor: "volume" },
								{
									label: "Volume (Mwh)",
									accessor: "volume_mwh",
								},
							]}
							items={[
								{
									type: "Clicked",
									volume: totalVolume.totalVolumePercentage,
									volume_mwh: totalVolume.totalVolumeAbsolute,
								},
								{
									type: "Free/Available",
									volume: totalVolume.availableVolumePercentage,
									volume_mwh:
										totalVolume.availableVolumeAbsolute,
								},
							]}
						/>
						<NJCard
							hasBottomBorder
							className=""
							data-testid="totalVolumeAbsolute"
						>
							<NJCardBody
								title={`${totalVolume.totalVolumeAbsolute} MWh`}
								data-testid="totalVolumeAbsoluteValue"
							>
								<p>Total volume to click</p>
							</NJCardBody>
						</NJCard>
					</Box>
				</Box>
				<Box
					sx={{
						border: "1px solid #EBEEF1",
						padding: "1rem",
					}}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<Typography fontSize={"1.2rem"} fontWeight={700}>
							Distribution of the clicks
						</Typography>
						<Toggle
							title="View by"
							listView={[
								{
									label: "Month",
									value: "month",
								},
								{
									label: "Quarter",
									value: "quarter",
								},
							]}
							activeView={viewBy}
							//@ts-ignore
							setActiveView={(newView) => {
								if (newView && newView !== viewBy)
									setViewBy(newView as ViewByT);
							}}
							isDisabled={containFullQuarter}
						></Toggle>
					</Box>
					
					{ sortedByDate[selectedYear] &&
						<BarsChartClicks
							sortedByDate={sortedByDate}
							selectedYear={selectedYear}
							viewBy={viewBy}
						/>
					}

					<SimpleTable
						dataTestid="clickabilityTableMounthly"
						headers={[
							{ label: "Period", accessor: "period" },
							{
								label: "Clicked Volume (%)",
								accessor: "volume_percentage",
							},
							{
								label: "Available Volume (%)",
								accessor: "volume_available",
							},
							{
								label: "Action",
								accessor: "action",
							},
						]}
						items={(sortedByDate[selectedYear]?.[viewBy] || []).map(
							(
								{
									period,
									click_start,
									volume_percentage,
									volume_available,
								}: MappedClicksDataT,
							) => ({
								period:
									viewBy === "month"
										? format(new Date(click_start), "MMMM")
										: period,
								volume_percentage: volume_percentage.toFixed(2),
								volume_available: volume_available.toFixed(2),
								action: (
                                    <NJButton
                                    emphasis="subtle"
                                    onClick={() => openModal(newClickOverview)}
                                    label={"Add click"}
                                    isDisabled={volume_percentage === 100}
                                />
								),
							})
						)}
					/>
                    <ClickModal
                    isOpen={isClickModalOpen}
                    isLoading={isCreatingClick}
                    newClickOverview={newClickOverview}
                    createClickErrorMessage={formatApiErrorMessage(
                        clickPostError as ErrorType
                    )}
                    clickPeriods={clickPeriods || []}
                    handleClose={() => {
                        closeModal();
                    }}
                    onCreateClick={(newClick: ClickFormik) => {
                        onCreateClickHelper(newClick, createClick, contractPeriod.id);
                    }}
                    contractPeriod={contractPeriod}
                    contract={contractData}
                    newClickUuid={uuidv4()}
                    totalVolumeAbsolute={totalVolume.totalVolumeAbsolute}
                    />
				</Box>
			</DialogContent>
			<DialogActions
				sx={{
					margin: "auto",
				}}
			>
				<NJButton onClick={handleClose} label="Close" icon="check">
					Close
				</NJButton>
			</DialogActions>
		</Dialog>
	);
}
